import type { TFunction } from 'i18next'
import type { ReminderAnchor, Task, TaskReminder } from '../db/models'
import { isStandardReminder } from '../db/models'
import { reminderLabel, reminderStatusLabel } from './reminderLabels'
import { anchorTime, nextReminderAt, reminderSilence, type ReminderSilence } from './schedule'

export interface ReminderRow {
  reminder: TaskReminder
  standard: boolean
  /** Was sie ist: „30 Min. vorher“. */
  label: string
  /** Wann sie als Nächstes klingelt, oder warum nicht. */
  status: { text: string; silent: boolean }
  next: Date | null
}

export interface TaskReminderRows {
  task: Task
  /** In der Reihenfolge des Anlegens — die Liste springt nicht, während man × drückt. */
  rows: ReminderRow[]
  /** Der früheste nächste Zeitpunkt aller — der Wert am Chip. */
  next: Date | null
  /** Wann Wann und Deadline sind — die Vorschläge im Reiter „Vorher“ rechnen davon. */
  anchors: Record<ReminderAnchor, Date | null>
  /** Warum eine „vorher“-Erinnerung am jeweiligen Bezug still bliebe; `null`, wenn sie klingeln kann. */
  anchorSilence: Record<ReminderAnchor, ReminderSilence | null>
  /** Vorlauf der Standard-Erinnerung in Minuten; `null` = aus. */
  defaultOffset: number | null
}

const probe = (task: Task, anchor: ReminderAnchor): TaskReminder => ({
  id: 'probe', taskId: task.id, kind: 'relative', anchor, offsetMinutes: 0, remindDate: null, remindTime: null,
  recurrence: null, snoozedUntil: null, importedFrom: null, userId: null, cloudId: null, deletedAt: null,
} as TaskReminder)

/**
 * **Die Erinnerungen einer Aufgabe, fertig beschriftet** (Plan task-reminders, Etappe 5) — rein gerechnet aus der
 * Kette der Aufgabe (`useTaskChains`), ihren Erinnerungen und dem Standard-Vorlauf. Desktop (`useTaskReminderRows`) und
 * Mobile-App (Erinnerungs-Sheet) teilen sie.
 */
export function reminderRowsOf(
  chain: readonly Task[],
  reminders: readonly TaskReminder[],
  defaultOffset: number | null,
  now: Date,
  t: TFunction,
  locale: string,
): TaskReminderRows {
  const task = chain[0]!
  const rows = reminders.map((reminder): ReminderRow => {
    const silence = reminderSilence(reminder, chain, defaultOffset)
    const next = silence ? null : nextReminderAt(reminder, chain, defaultOffset, now)
    const snoozed = !!next && !!reminder.snoozedUntil && next.getTime() === reminder.snoozedUntil.getTime()
    return {
      reminder,
      standard: isStandardReminder(reminder),
      label: reminderLabel(reminder, defaultOffset, now, t, locale),
      status: reminderStatusLabel({ next, silence, snoozed }, now, t, locale),
      next,
    }
  })
  const upcoming = rows.map(row => row.next).filter((d): d is Date => d !== null).sort((a, b) => a.getTime() - b.getTime())
  return {
    task,
    rows,
    next: upcoming[0] ?? null,
    anchors: { start: anchorTime(task, 'start'), deadline: anchorTime(task, 'deadline') },
    anchorSilence: {
      start: reminderSilence(probe(task, 'start'), chain, defaultOffset),
      deadline: reminderSilence(probe(task, 'deadline'), chain, defaultOffset),
    },
    defaultOffset,
  }
}
